import { useState } from "react"
import { Box, Button, Divider, Stack, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { Bound } from "lib/@components/binding/Bound"
import { ListItemBox } from "lib/@components/ListItemBox"
import { StandardSelector } from "lib/@components/combos/standard-selector"
import { SearchBoxUsingSearchParam } from "lib/@components/searchBoxUsingSearchParam"
import { RequestPageRepeat } from "lib/@components/request-page-repeat"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { useReferenceState } from "lib/@hooks/use-reference"
import { emptyArray } from "lib/empty-array"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import { busyWhile } from "lib/@components/busy/busy-while"
import { confirm } from "lib/@dialogs/confirm"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import Iconify from "minimals-template/components/Iconify"
import { rateCards } from "routes/rate-cards/controller/rate-cards"
import { archiveRateCard } from "routes/rate-cards/controller/archive-rate-card"
import { restoreRateCard } from "routes/rate-cards/controller/restore-rate-card"
import { RateCardRow } from "./rate-card-row"
import { RateCardTableHeader } from "./rate-card-table-header"
import { RateCardsEmpty } from "./rate-cards-empty"
import { useCreateRateCard } from "./use-create-rate-card"
import { useDemand } from "lib/authorization/use-demand"

const sortOptions = [
    { value: { sort: "created", descending: true }, label: "Latest" },
    { value: { sort: "created", descending: false }, label: "Oldest" },
    { value: { sort: "name", descending: false }, label: "Name" },
    { value: { sort: "name", descending: true }, label: "Name (desc)" },
    { value: { sort: "modified", descending: true }, label: "Modified" },
    { value: { sort: "modified", descending: false }, label: "Modified (asc)" },
]

const statusOptions = [
    { value: "active", label: "Active" },
    { value: "archived", label: "Archived" },
    { value: "all", label: "All" },
]

export function RateCardsTab() {
    const canManageOptimiser = useDemand("manageScenarios")
    const handleCreate = useCreateRateCard()
    const [skip, setSkip] = useState(0)
    const [selectedIds, setSelectedIds] = useState(makeInitialSelectedIds)
    const [search] = useSearchParam("search")
    const [status, setStatus] = useSearchParam("rcStatus", "active")
    const [storedSortMode, setSortMode] = useReferenceState("rateCardsSort", sortOptions[0].value)
    const sortMode = (
        sortOptions.find(
            (f) => f.value.sort === storedSortMode?.sort && f.value.descending === storedSortMode?.descending
        ) ?? sortOptions[0]
    )?.value
    const result = rateCards.useResults({
        skip,
        take: 5,
        search,
        status,
        ...sortMode,
    })
    const { items, totalCount } = result ?? { items: emptyArray, totalCount: 0 }
    const archivedResult = rateCards.useResults({ skip: 0, take: 0, status: "archived" })
    const archivedCount = archivedResult?.totalCount ?? 0

    return (
        <If value={items !== emptyArray}>
            <If value={totalCount > 0 || search}>
                <Stack data-cy="rate-cards-list" spacing={2} sx={{ pt: 1.5 }}>
                    <Typography variant="h5" sx={{ fontSize: 18 }}>
                        Rate Cards
                    </Typography>
                    <ListItemBox spacing={1.5} sx={{ alignItems: "center" }}>
                        <If value={status !== "archived" && canManageOptimiser}>
                            <Button
                                variant="contained"
                                startIcon={<Iconify icon="mdi:plus" />}
                                onClick={handleCreate}
                                sx={{ whiteSpace: "nowrap" }}
                            >
                                Create Rate Card
                            </Button>
                        </If>
                        <Box flex={1} />
                        <StandardSelector
                            sortTitle="Status:"
                            options={statusOptions}
                            value={status}
                            onChange={handleStatusChange}
                            sx={{
                                fontSize: "85%",
                                color: "text.primary",
                                whiteSpace: "nowrap",
                                textTransform: "none",
                                fontWeight: "normal",
                            }}
                        />
                        <Divider orientation="vertical" flexItem />
                        <StandardSelector
                            sortTitle="Sort:"
                            options={sortOptions}
                            value={sortMode}
                            onChange={setSortMode}
                            sx={{
                                fontSize: "85%",
                                color: "text.primary",
                                whiteSpace: "nowrap",
                                textTransform: "none",
                                fontWeight: "normal",
                            }}
                        />
                    </ListItemBox>
                    <SearchBoxUsingSearchParam autoFocus={false} placeholder="Search..." sx={{ py: 0, m: 0 }} />
                    <Bound
                        selectedIds={selectedIds}
                        onToggleSelect={handleToggleSelect}
                        onSelectAll={handleSelectAll}
                        pageItems={items}
                        canManageOptimiser={canManageOptimiser}
                    >
                        <If value={canManageOptimiser && selectedIds.size > 0}>
                            <ListItemBox
                                data-cy="bulk-action-toolbar"
                                sx={{
                                    px: 2,
                                    py: 1,
                                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16),
                                    borderRadius: 1,
                                }}
                            >
                                <Typography variant="body2" fontWeight={600} color="text.primary">
                                    {selectedIds.size} selected
                                </Typography>
                                <Box flex={1} />
                                <Button
                                    size="small"
                                    variant="text"
                                    color="primary"
                                    onClick={handleClearSelection}
                                    sx={{ mr: 1 }}
                                >
                                    Clear
                                </Button>
                                {/* Archived cards can only be restored in bulk; active/all keep the archive action */}
                                <If value={status === "archived"}>
                                    <Button
                                        size="small"
                                        variant="text"
                                        color="primary"
                                        startIcon={<Iconify icon="solar:restart-bold" />}
                                        onClick={handleBulkRestore}
                                    >
                                        Restore
                                    </Button>
                                    <Else>
                                        <Button
                                            size="small"
                                            variant="text"
                                            color="error"
                                            startIcon={<Iconify icon="solar:archive-bold" />}
                                            onClick={handleBulkArchive}
                                        >
                                            Archive
                                        </Button>
                                    </Else>
                                </If>
                            </ListItemBox>
                        </If>
                        <Stack spacing={0}>
                            <RateCardTableHeader />
                            <RequestPageRepeat
                                bind
                                refreshKey={`${search}-${status}-${sortMode?.sort}-${sortMode?.descending}`}
                                total={totalCount}
                                list={items}
                                setSkip={setSkip}
                                pageSize={5}
                                item={<RateCardRow />}
                            />
                        </Stack>
                    </Bound>
                </Stack>
                <Else>
                    <RateCardsEmpty
                        status={status}
                        archivedCount={archivedCount}
                        onViewArchived={handleViewArchived}
                        onViewActive={handleViewActive}
                        canManageOptimiser={canManageOptimiser}
                    />
                </Else>
            </If>
            <Else>
                <LoadingScreen description="Retrieving Rate Cards" />
            </Else>
        </If>
    )

    function makeInitialSelectedIds() {
        return new Set()
    }

    function isItemSelected(i) {
        return selectedIds.has(i.id)
    }

    function handleToggleSelect(id) {
        if (!canManageOptimiser) return
        setSelectedIds((prev) => {
            const next = new Set(prev)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })
    }

    function handleSelectAll() {
        if (!canManageOptimiser) return
        const allSelected = items.length > 0 && items.every(isItemSelected)
        setSelectedIds(allSelected ? new Set() : new Set(items.map((i) => i.id)))
    }

    function handleClearSelection() {
        setSelectedIds(new Set())
    }

    async function handleBulkArchive() {
        const count = selectedIds.size
        const ok = await confirm(`Archive ${count} rate card${count === 1 ? "" : "s"}?`, "Archive Rate Cards")
        if (!ok) return
        await busyWhile(
            async () => {
                for (const id of selectedIds) await archiveRateCard({ id })
            },
            `Archiving ${count} rate card${count === 1 ? "" : "s"}...`
        )
        setSelectedIds(new Set())
        successSnackbar(`${count} rate card${count === 1 ? "" : "s"} archived`)
    }

    async function handleBulkRestore() {
        const count = selectedIds.size
        const ok = await confirm(`Restore ${count} rate card${count === 1 ? "" : "s"}?`, "Restore Rate Cards")
        if (!ok) return
        await busyWhile(
            async () => {
                for (const id of selectedIds) await restoreRateCard({ id })
            },
            `Restoring ${count} rate card${count === 1 ? "" : "s"}...`
        )
        setSelectedIds(new Set())
        successSnackbar(`${count} rate card${count === 1 ? "" : "s"} restored`)
    }

    function handleStatusChange(value) {
        // Selection is scoped to the current status filter; clear it so a bulk action can't run against stale rows
        setStatus(value)
        setSelectedIds(new Set())
    }

    function handleViewArchived() {
        setStatus("archived")
    }

    function handleViewActive() {
        setStatus("active")
    }
}
